import { Link, useRouterState, useRouter } from "@tanstack/react-router";
import { AppShell, NavLink, Text, Divider, Box, ScrollArea } from "@mantine/core";
import {
  IconHome,
  IconBuilding,
  IconFilter,
  IconSettings,
  IconRefresh,
  IconBell,
  IconX,
  IconLogout,
} from "@tabler/icons-react";
import { ScrapeStatus } from "./ScrapeStatus";
import { useAuth } from "@/context/AuthContext";

const navGroups = [
  {
    label: "Main",
    links: [
      { to: "/", label: "Dashboard", icon: IconHome },
      { to: "/apartments", label: "Apartments", icon: IconBuilding },
    ],
  },
  {
    label: "Configuration",
    links: [
      { to: "/filters", label: "Filters", icon: IconFilter },
      { to: "/settings", label: "Settings", icon: IconSettings },
    ],
  },
  {
    label: "Monitoring",
    links: [{ to: "/scrapes", label: "Scrapes", icon: IconRefresh }],
  },
  {
    label: "Admin",
    links: [
      { to: "/admin/notifications", label: "Notifications", icon: IconBell },
      { to: "/admin/rejections", label: "Rejections", icon: IconX },
    ],
  },
] as const;

interface SidebarProps {
  onClose?: () => void;
}

export function Sidebar({ onClose }: SidebarProps) {
  const { logout } = useAuth();
  const router = useRouter();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const handleLogout = async () => {
    await logout();
    router.navigate({ to: "/login" });
  };

  return (
    <>
      <AppShell.Section p="md">
        <Text c="primary" fw={700} size="xl">
          Qasa Notifier
        </Text>
        <Box mt="sm">
          <ScrapeStatus />
        </Box>
      </AppShell.Section>

      <Divider color="#0f3460" />

      <AppShell.Section grow component={ScrollArea} p="sm">
        {navGroups.map((group) => (
          <Box key={group.label} mb="md">
            <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb="xs" px="sm">
              {group.label}
            </Text>
            {group.links.map((link) => (
              <NavLink
                key={link.to}
                component={Link}
                to={link.to}
                label={link.label}
                leftSection={<link.icon size={18} stroke={1.5} />}
                active={currentPath === link.to}
                onClick={onClose}
                color="primary"
                variant="filled"
                styles={{
                  root: {
                    borderRadius: "0.375rem",
                    "--nl-bg": "transparent",
                    "--nl-hover": "#0f3460",
                    "--nl-color": "#eeeeee",
                  },
                }}
              />
            ))}
          </Box>
        ))}
      </AppShell.Section>

      <Divider color="#0f3460" />

      <AppShell.Section p="sm">
        <NavLink
          label="Logout"
          leftSection={<IconLogout size={18} stroke={1.5} />}
          onClick={handleLogout}
          color="red"
          variant="subtle"
          styles={{
            root: { borderRadius: "0.375rem" },
          }}
        />
      </AppShell.Section>
    </>
  );
}
